import * as Schema from "effect/Schema";

import { EnvironmentId, ThreadId } from "./baseSchemas";

/**
 * Contract for the Forge computer-use surface (`forge_computer_*`).
 *
 * The tool surface lives in `apps/server/src/computer/`; the native execution and
 * the macOS TCC preflight live in `apps/desktop/src/nativeComputerHostBridge.ts`.
 * This module is the only shared vocabulary between them, mirroring how the
 * browser surface splits server-side tools from the native host runtime.
 *
 * Schema-only: no runtime logic (see AGENTS.md "Package Roles").
 */

export const ComputerMouseButton = Schema.Literals(["left", "right", "middle"]);
export type ComputerMouseButton = typeof ComputerMouseButton.Type;

/**
 * macOS gates screen capture and synthetic input behind two distinct TCC grants.
 * They are requested separately and can be granted independently, so a caller
 * may hold one and not the other.
 */
export const ComputerPermissionKind = Schema.Literals(["screen-recording", "accessibility"]);
export type ComputerPermissionKind = typeof ComputerPermissionKind.Type;

const Coordinate = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0));
const Delta = Schema.Int;

export const ComputerCommand = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("screenshot") }),
  Schema.Struct({ kind: Schema.Literal("cursor_position") }),
  Schema.Struct({
    kind: Schema.Literal("click"),
    x: Coordinate,
    y: Coordinate,
    button: Schema.optional(ComputerMouseButton),
  }),
  Schema.Struct({
    kind: Schema.Literal("double_click"),
    x: Coordinate,
    y: Coordinate,
    button: Schema.optional(ComputerMouseButton),
  }),
  Schema.Struct({ kind: Schema.Literal("type"), text: Schema.String }),
  Schema.Struct({ kind: Schema.Literal("key"), combo: Schema.String }),
  Schema.Struct({
    kind: Schema.Literal("scroll"),
    x: Coordinate,
    y: Coordinate,
    deltaX: Delta,
    deltaY: Delta,
  }),
  Schema.Struct({
    kind: Schema.Literal("drag"),
    fromX: Coordinate,
    fromY: Coordinate,
    toX: Coordinate,
    toY: Coordinate,
    button: Schema.optional(ComputerMouseButton),
  }),
  /**
   * What is on the display, so an agent can work with native windows instead of
   * guessing coordinates from a screenshot.
   */
  Schema.Struct({ kind: Schema.Literal("list_windows") }),
  /**
   * Start an application on the runtime display.
   *
   * `args` is a list rather than a shell string on purpose: the backend passes
   * it straight to exec, so there is no shell to quote for and no injection
   * surface from a filename with a space or a semicolon in it.
   */
  Schema.Struct({
    kind: Schema.Literal("launch"),
    command: Schema.String,
    args: Schema.optional(Schema.Array(Schema.String)),
  }),
  /**
   * Record a bounded segment of the display to a .webm file and return its path.
   *
   * `seconds` is a request, not a guarantee: the backend clamps it, because an
   * unbounded recording fills the runtime's writable allowance and every
   * subsequent write on the box fails.
   */
  Schema.Struct({
    kind: Schema.Literal("record"),
    seconds: Schema.optional(Schema.Number),
  }),
]);
export type ComputerCommand = typeof ComputerCommand.Type;

export const ComputerResult = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("screenshot"),
    mimeType: Schema.Literal("image/png"),
    base64: Schema.String,
    width: Schema.Number,
    height: Schema.Number,
  }),
  Schema.Struct({
    kind: Schema.Literal("cursor_position"),
    x: Schema.Number,
    y: Schema.Number,
  }),
  Schema.Struct({ kind: Schema.Literal("ok"), detail: Schema.String }),
  Schema.Struct({
    kind: Schema.Literal("windows"),
    windows: Schema.Array(
      Schema.Struct({
        id: Schema.String,
        title: Schema.String,
        x: Schema.Number,
        y: Schema.Number,
        width: Schema.Number,
        height: Schema.Number,
      }),
    ),
  }),
  Schema.Struct({
    kind: Schema.Literal("recording"),
    path: Schema.String,
    seconds: Schema.Number,
    /** True when the duration cap had to kill the encoder, so the file is partial. */
    truncated: Schema.Boolean,
  }),
  /**
   * A missing TCC grant is a normal, typed outcome — never a thrown error and
   * never a silently empty screenshot. The agent is told which macOS setting to
   * open so it can ask the user instead of retrying blind.
   */
  Schema.Struct({
    kind: Schema.Literal("permission_required"),
    permission: ComputerPermissionKind,
    message: Schema.String,
    settingsUrl: Schema.String,
  }),
]);
export type ComputerResult = typeof ComputerResult.Type;

export const COMPUTER_PERMISSION_SETTINGS_URL: Readonly<Record<ComputerPermissionKind, string>> = {
  "screen-recording":
    "x-apple.systempreferences:com.apple.preference.security?Privacy_ScreenCapture",
  accessibility: "x-apple.systempreferences:com.apple.preference.security?Privacy_Accessibility",
};

export function computerPermissionRequired(
  permission: ComputerPermissionKind,
): Extract<ComputerResult, { kind: "permission_required" }> {
  return {
    kind: "permission_required",
    permission,
    message:
      permission === "screen-recording"
        ? "Forge needs macOS Screen Recording permission to capture the screen. Grant it in System Settings > Privacy & Security > Screen Recording, then retry."
        : "Forge needs macOS Accessibility permission to control the mouse and keyboard. Grant it in System Settings > Privacy & Security > Accessibility, then retry.",
    settingsUrl: COMPUTER_PERMISSION_SETTINGS_URL[permission],
  };
}

/**
 * A human driving the runtime desktop directly, rather than an agent calling
 * `forge_computer_*`.
 *
 * Both paths land on the same {@link ComputerCommand} and the same execution
 * boundary, so a click the user makes and a click the agent makes are the same
 * operation on the same display. Only the caller differs, and only the caller
 * is authorized differently — this input carries a thread so the server can
 * scope the command the way every other browser-control RPC is scoped.
 */
export const ComputerInputInput = Schema.Struct({
  threadId: ThreadId,
  environmentId: Schema.optionalKey(EnvironmentId),
  command: ComputerCommand,
});
export type ComputerInputInput = typeof ComputerInputInput.Type;

export const ComputerInputResult = Schema.Struct({
  result: ComputerResult,
});
export type ComputerInputResult = typeof ComputerInputResult.Type;

/**
 * Raised when the server has no computer backend at all.
 *
 * Distinct from `permission_required`, which is a *typed success*: the backend
 * exists and told us precisely what is missing. This error means there is
 * nothing to ask — no host bridge is attached and the process is not running
 * inside a runtime with an X display — so the caller should stop rather than
 * retry or prompt.
 */
export class ComputerError extends Schema.TaggedErrorClass<ComputerError>()("ComputerError", {
  detail: Schema.String.check(Schema.isNonEmpty()),
}) {}
